import React, { useState, useEffect } from "react";
import { Page } from "../../components/Page";
import { getCurrentUser, getRoomType, deleteRoomType } from "../../api-config/config";
import NewRoomType from "../../components/Rooms/RoomTypes";
import SimpleTable from "../../components/table/SimpleTable";
import SnackBar from "../../components/SnackBar/SnackBar";
import LoaderWrapper from "../../components/LoaderWrapper/LoaderWrapper";

function RoomTypeListGrid(props: any) {
  const { loggedUser, setLoading, roomTypeAdded, showSnackbar } = props;

  const [open, setOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [roomTypeData, setRoomTypeData] = useState<any>({});
  const [localRows, setLocalRows] = useState<any[]>([]);

  const onEditClick = (rowData: any) => {
    setRoomTypeData(rowData);
    setOpen(true);
  };

  const onDeleteClick = (row: any) => {
    setRoomTypeData(row);
    setDeleteOpen(true);
  };

  const handleDeleteConfirm = async () => {
    const backup = localRows;

    setLocalRows((prev) =>
      prev.filter((r) => r.room_type_id !== roomTypeData.room_type_id)
    );

    try {
      setLoading(true);
      await deleteRoomType(roomTypeData.room_type_id, roomTypeData.hotel_code);
      if (showSnackbar) {
        showSnackbar("Deleted Room Type successfully", "success", () => {
          roomTypeAdded();
        });
      } else {  
        roomTypeAdded();
      }
    } catch {
      setLocalRows(backup);
      if (showSnackbar) {
        showSnackbar("Failed to Delete Room Type", "error");
      }
    } finally {
      setLoading(false);
      setDeleteOpen(false);
      setRoomTypeData({});
    }
  };


  const columns = [
    { header: "#", accessor: "serial", width: "5%" },
    { header: "Room Type", accessor: "roomtype", width: "20%" },
    { header: "Occupancy", accessor: "occupancy", width: "10%" },
    { header: "Rooms", accessor: "rooms", width: "10%" },
    { header: "Extra Bed(s)", accessor: "extra", width: "15%" },
    { header: "Status", accessor: "status", width: "10%" },
    {
      header: "Actions",
      accessor: "actions",
      width: "15%",
      render: (_: any, row: any) => (
        <div style={{ display: "flex", justifyContent: "center", gap: 16 }}>
          {/* Edit */}
          <span style={{ cursor: "pointer" }} onClick={() => onEditClick(row)}>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
            </svg>
          </span>

          {/* Delete */}
          <span style={{ cursor: "pointer" }} onClick={() => onDeleteClick(row)}>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 30 30" fill="red">
              <path d="M 14.984375 2.4863281 A 1.0001 1.0001 0 0 0 14 3.5 L 14 4 L 8.5 4 A 1.0001 1.0001 0 0 0 7.4863281 5 L 6 5 A 1.0001 1.0001 0 1 0 6 7 L 24 7 A 1.0001 1.0001 0 1 0 24 5 L 22.513672 5 A 1.0001 1.0001 0 0 0 21.5 4 L 16 4 L 16 3.5 A 1.0001 1.0001 0 0 0 14.984375 2.4863281 z M 6 9 L 7.7929688 24.234375 C 7.9109687 25.241375 8.7633438 26 9.7773438 26 L 20.222656 26 C 21.236656 26 22.088031 25.241375 22.207031 24.234375 L 24 9 L 6 9 z"/>
            </svg>
          </span>
        </div>
      ),
    },
  ];

  useEffect(() => {
    if (!props.row) return;

    const rows = props.row.map((item: any, index: number) => ({
      serial: index + 1,
      roomtype: item.room_type_name,
      occupancy: item.higher_occupancy,
      rooms: item.rooms?.length || 0,
      extra: item.extra_bed,
      status: item.is_active ? "Active" : "Inactive",
      room_type_id: item.id,
      hotel_code: loggedUser?.hotel_code,
      base_occupancy: item.base_occupancy,
      stock: item.no_rooms_stock,
      price: item.price,
      extra_bed_price: item.extra_bed_price,
      amenities_ids: item.amenities_ids,
      tax_ids: item.tax_ids,
      service_charge: item.service_charge,
    }));

    setLocalRows(rows);
  }, [props.row, loggedUser]);

  return (
    <div style={{ width: "100%" }}>
      <div className="roomtype-header">
        <h2 className="roomtype-title">Room Type List</h2>
        <div className="roomtype-btns">
          <button className="my-custom-btn" onClick={() => { setRoomTypeData({}); setOpen(true); }}>
            Add Room Type
          </button>
        </div>
      </div>

      <SimpleTable columns={columns} data={localRows} />

      {open && (
        <div className="modal-overlay" onClick={() => setOpen(false)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Room Type</h3>
            </div>
            <div className="modal-content">
              <NewRoomType
                handleClose={() => { setOpen(false); setRoomTypeData({}); }}
                onRoomTypeAdded={roomTypeAdded}
                roomTypeData={roomTypeData}
                setLoading={setLoading}
                showSnackbar={showSnackbar}
              />
            </div>
          </div>
        </div>
      )}

      {deleteOpen && (
        <div className="delete-modal-overlay" onClick={() => setDeleteOpen(false)}>
          <div className="delete-modal-box" onClick={(e) => e.stopPropagation()}>
            <h3 className="delete-modal-title">Are you sure?</h3>

            <p className="delete-modal-desc">
              Room Type <span>{roomTypeData.roomtype}</span> will be deleted.
            </p>

            <div className="delete-modal-actions">
              <button className="delete-btn-cancel" onClick={() => setDeleteOpen(false)}>
                No
              </button>
              <button className="delete-btn-confirm" onClick={handleDeleteConfirm}>
                Yes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function NewRoomTypeList() {
  const [roomList, setRoomList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loggedUser = getCurrentUser();

  const [snackbar, setSnackbar] = useState({
    visible: false,
    message: "",
    type: "success" as "success" | "error",
  });

  const showSnackbar = (msg: string, type: "success" | "error", callback?: () => void) => {
    setSnackbar({ visible: true, message: msg, type });
    if (callback) {
      callback();
    }
    setTimeout(() => {
      setSnackbar((prev) => ({ ...prev, visible: false }));
    }, 3000);
  };


  const load = () => {
    setLoading(true);
    setError(null);

    getRoomType(loggedUser?.hotel_code, 1)
      .then((res: any) => {
        if (res?.status_message === "success") {
          setRoomList(res.data || []);
        }
      })
      .catch(() => {
        setError("Failed to load Room Types");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <Page title="Home">
      <div style={{ padding: "0 20px" }}>
        {loading ? (
          <LoaderWrapper />
        ) : error ? (
          <div style={{ color: "red", padding: 24 }}>{error}</div>
        ) : (
          <RoomTypeListGrid
            row={roomList}
            roomTypeAdded={load}
            loggedUser={loggedUser}
            setLoading={setLoading}
            showSnackbar={showSnackbar}
          />
        )}
        
        <SnackBar
          visible={snackbar.visible}
          message={snackbar.message}
          type={snackbar.type}
          onClose={() => {
            setSnackbar((prev) => ({ ...prev, visible: false }));
          }}
        />
      </div>
    </Page>
  );
}